import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Lock, ShieldAlert } from "lucide-react";
import { Switch } from "./ui/switch";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Het schakelbord: welke grenzen open mogen, en sinds wanneer. ONDERDEEL 3.
 *
 * MASTERPLAN 3ax-3: "wat aan staat, wat uit, sinds wanneer, en wat er sindsdien
 * onder die stand is gebeurd."
 *
 * FRANKS BESLUIT VAN 1 SEPTEMBER 2026, en het is de vorm van dit scherm:
 * "Alleen soort 1 krijgt een schakelaar. Soort 2 en 3 blijven vast. Een grens
 * die het antwoord onjuist maakt zonder dat ik het zie, is geen keuze maar een
 * val."
 *
 * DAAROM STAAT ER ONDERAAN MET ZOVEEL WOORDEN WAT HIER NIET STAAT. Zwijgen zou
 * lezen als "dit is alles wat er is". De twaalf antwoordwachters bestaan wel;
 * zij hebben hier alleen geen knop, en dat is een besluit dat uitgelegd hoort te
 * worden op de plek waar iemand ernaar zoekt.
 *
 * DE STAND KOMT UIT DE BACKEND EN NIET UIT DIT SCHERM. Een schakelaar die een
 * scherm kan verzinnen is geen schakelaar; de weigering staat in
 * `services/grenzen.py::eis_schakelbaar`, op de schrijfweg.
 */

/** Een datum zoals een mens hem leest, of leeg. */
/** De vaste reden van de eenmalige overname komt van ons en wordt vertaald.
 * Een reden die iemand zelf typte, blijft zoals hij is. */
export const OVERNAME_REDEN = "overgenomen uit Instellingen (fix 12, eenmalig)";
export function eigenReden(t, reden) {
  return reden === OVERNAME_REDEN ? t(OVERNAME_REDEN) : reden;
}

function alsDatum(iso, taal) {
  if (!iso) return "";
  try {
    // R2: de maandnaam in de taal van de schakelaar, niet die van de browser.
    return new Date(iso).toLocaleString(taal === "en" ? "en-GB" : "nl-NL", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      // DE TIJDZONE STAAT ER EXPLICIET NIET OP `UTC`. Dit is een moment in de
      // tijd en geen kale datum, dus het hoort in de zone van de lezer te
      // staan. (Bij een kale datum zou juist `timeZone: "UTC"` moeten, zie
      // huisregel 4.11.)
    });
  } catch {
    return "";
  }
}

export default function Grenzen() {
  const { t, taal } = useTaal();
  const [grenzen, setGrenzen] = useState(null);
  // PUNT 88, Franks besluit van 1 september 2026. De vijf grenzen die WEL
  // bestaan en geen knop hebben, met de reden per stuk. Zij komen uit DEZELFDE
  // route en niet uit een tweede: een lijst hier zou verlopen ten opzichte van
  // `grenzen.ZONDER_KNOP`, die hem waar maakt.
  const [zonderKnop, setZonderKnop] = useState([]);
  const [bezig, setBezig] = useState("");

  const laden = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/grenzen`);
      setGrenzen(data.grenzen || []);
      setZonderKnop(data.zonder_knop || []);
    } catch {
      setGrenzen([]);
      toast.error(t("De grenzen konden niet worden opgehaald."));
    }
  }, [t]);

  useEffect(() => { laden(); }, [laden]);

  async function omzetten(grens) {
    setBezig(grens.sleutel);
    try {
      await axios.put(`${API}/grenzen`, {
        sleutel: grens.sleutel,
        open: !grens.open,
        gebied: "",
        reden: "",
      });
      await laden();
    } catch (e) {
      // De backend weigert alles wat vast hoort te blijven, en die reden is
      // leesbaar geschreven. Hem hier vervangen door "er ging iets mis" zou
      // precies de uitleg weggooien die het antwoord waardevol maakt.
      toast.error(e?.response?.data?.detail || t("Omzetten is niet gelukt."));
    } finally {
      setBezig("");
    }
  }

  if (grenzen === null) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t("Grenzen ophalen...")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {grenzen.map((g) => (
          <div
            key={g.sleutel}
            data-testid={`grens-${g.sleutel}`}
            className="flex items-start justify-between gap-4 rounded-lg border p-3"
          >
            <div className="min-w-0 space-y-1">
              <div className="font-medium">{g.naam}</div>
              <div className="text-xs text-muted-foreground break-words">
                {g.open ? t("staat open") : t("staat dicht")}
                {g.sinds ? ` - ${t("sinds {wanneer}", { wanneer: alsDatum(g.sinds, taal) })}` : ""}
                {g.reden ? ` - ${eigenReden(t, g.reden)}` : ""}
                {g.uit_instellingen ? ` - ${t("overgenomen uit Instellingen")}` : ""}
              </div>
              {/* ALLEEN TONEN ALS ER IETS TE TONEN IS. Een lege lijst met een
                  kopje erboven leest als "er is niets gebeurd" terwijl het
                  betekent "hier is nooit iets omgezet", en dat zijn twee
                  verschillende dingen. */}
              {g.geschiedenis && g.geschiedenis.length > 0 && (
                <ul className="text-xs text-muted-foreground/80 space-y-0.5 pt-1">
                  {g.geschiedenis.map((h) => (
                    <li key={`${g.sleutel}-${h.gebied}-${h.versie}`}>
                      {t("versie {n}", { n: h.versie })}: {h.open ? t("open") : t("dicht")}
                      {h.gebied ? ` ${t("voor {gebied}", { gebied: h.gebied })}` : ""}
                      {h.moment ? ` ${t("op {wanneer}", { wanneer: alsDatum(h.moment, taal) })}` : ""}
                      {h.reden ? ` - ${eigenReden(t, h.reden)}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Switch
              data-testid={`schakel-${g.sleutel}`}
              checked={!!g.open}
              disabled={bezig === g.sleutel}
              onCheckedChange={() => omzetten(g)}
              aria-label={`${g.naam} ${g.open ? t("dichtzetten") : t("openzetten")}`}
            />
          </div>
        ))}
      </div>

      {zonderKnop.length > 0 && (
        <div data-testid="grenzen-zonder-knop" className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground">
            {t("Deze grenzen bestaan wel en hebben geen knop")}
          </div>
          {zonderKnop.map((g) => (
            <div
              key={g.sleutel}
              data-testid={`geen-knop-${g.sleutel}`}
              className="rounded-lg border border-dashed p-3 space-y-1"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {g.naam}
              </div>
              <p className="text-xs text-muted-foreground">{g.waarom_geen_knop}</p>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            {t("Een schakelaar voor iets dat niet bestaat is decoratie. Bij deze vijf zit de grens niet in een slot dat open kan, maar in een vermogen dat er niet is - en dat is de sterkere vorm.")}
          </p>
        </div>
      )}

      <div
        data-testid="grenzen-uitleg"
        className="flex gap-3 rounded-lg border border-dashed p-3 text-xs text-muted-foreground"
      >
        <Lock className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p>
            {t("Twee soorten grens staan hier met opzet niet en blijven")} <b>{t("vast")}</b>.
          </p>
          <p>
            {t("De grenzen die de")} <b>{t("integriteit")}</b> {t("bewaken - het slot op de vault, de vingerafdruk, de rem op te veel krimp - leveren niets op als zij uit staan: er mag dan niets extra's, er wordt alleen stiller vernield.")}
          </p>
          <p>
            {t("En de grenzen die de")} <b>{t("juistheid van een antwoord")}</b> {t("bewaken. Uit zetten maakt een antwoord niet vrijer, alleen ongemerkter onjuist.")}
          </p>
        </div>
      </div>

      <div className="flex gap-3 text-xs text-muted-foreground">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
        <p>
          {t("Staat er een grens open, dan zie je dat bovenin het chatscherm aan het teken \"Open: ...\". Zo is het ook te zien voor wie dit scherm niet opent.")}
        </p>
      </div>
    </div>
  );
}
